import { useCallback, useEffect, useState } from "react";
import { Plant, Storefront, Sparkle, Crosshair, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageCaptionField } from "@/components/ImageCaptionField";
import { PickYourOwn } from "@/components/PickYourOwn";
import { MilkRound } from "@/components/MilkRound";
import { FreshCatch } from "@/components/FreshCatch";
import { FreshMeat } from "@/components/FreshMeat";
import { useWhere } from "@/components/RideExtras";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Farmers() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stallId, setStallId] = useState("");
  const [method, setMethod] = useState("");
  const [stall, setStall] = useState({ name: "", where: "", about: "", organic: true, collection: true, delivery: true, delivery_radius_miles: 5, delivery_fee: 0, free_delivery_over: 0, market_day: "" });
  const [lot, setLot] = useState({ kind: "eggs", title: "", price: 0, unit: "dozen", quantity: 12, note: "", organic: true, image_id: "", image_caption: "" });
  const [buy, setBuy] = useState({});
  const [how, setHow] = useState("collection");
  const [pay, setPay] = useState("online");
  const [address, setAddress] = useState("");
  const { where, take, busy: locating } = useWhere();

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (stallId) q.set("stall_id", stallId);
    if (method) q.set("method", method);
    const qs = q.toString();
    api.get(`/farm${qs ? `?${qs}` : ""}`)
      .then(({ data: d }) => setData(d)).catch(oops);
  }, [stallId, method]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const sendProof = async (orderId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const form = new FormData();
      form.append("file", file);
      form.append("caption", "Picked today");
      try {
        const { data: up } = await api.post("/images", form);
        await api.post(`/farm/orders/${orderId}/picked`, { image_id: up.id, note: "Picked this morning" });
        toast.success("Photo sent to the buyer");
        load();
      } catch (e) { oops(e); }
    };
    input.click();
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Loading the market…</p>;
  const mine = data.my_stalls?.[0];
  const basket = Object.entries(buy).filter(([, v]) => Number(v?.qty) > 0);
  const basketStall = basket.length ? data.produce.find((p) => p.id === basket[0][0])?.stall_id : null;

  return (
    <div className="space-y-6" data-testid="farmers-page">
      <div>
        <p className="overline text-[#B3241B]">Local farmers market</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <Plant size={30} weight="duotone" className="text-[#0B5C2E]" /> From the back garden
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="farmers-explainer">
          Eggs, bread, vegetables, honey and jam, priced by the people who grew it. Sellers put
          their own price on; buyers can take it or write in what they are willing to pay. The
          photograph is of the actual item, taken the morning it was picked.
        </p>
      </div>

      <div className="border-l-2 border-[#0B5C2E] bg-[#F3FAF5] px-4 py-3" data-testid="farmers-board-notice">
        <p className="text-sm leading-relaxed text-slate-700">
          Whatever you put up — a photograph of the actual item and your own price — goes
          straight onto the{" "}
          <a href="/farmers-market" target="_blank" rel="noopener noreferrer"
            className="font-semibold text-[#0B5C2E] underline" data-testid="farmers-board-link">
            public market board
          </a>{" "}
          for anyone to see and buy from, with the share buttons for Facebook and WhatsApp.
          {mine && !mine.verified && (
            <span className="mt-1 block font-semibold text-[#B3241B]" data-testid="farmers-pending-notice">
              {mine.name} is waiting to be checked over — your items are saved and will appear
              on the public board the moment it is approved.
            </span>
          )}
        </p>
      </div>

      <div className="border border-border bg-white p-4" data-testid="stall-box">
        <p className="overline text-slate-500">
          <Storefront size={13} className="mr-1 inline align-[-2px] text-[#0B5C2E]" /> Stalls
        </p>
        <select data-testid="stall-pick" value={stallId} aria-label="Stall"
          onChange={(e) => setStallId(e.target.value)}
          className="tap-safe mt-2 w-full border border-border bg-white px-3 py-2 text-sm">
          <option value="">Every stall</option>
          {data.stalls.map((s) => <option key={s.id} value={s.id}>{`${s.name} · ${s.where || ""}`}</option>)}
        </select>

        <div className="mt-4 border-t border-border pt-3">
          <p className="overline text-slate-500">Open your own stall</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Input data-testid="stall-name" placeholder="Stall name — 'Hilltop Hens'"
              value={stall.name} onChange={(e) => setStall({ ...stall, name: e.target.value })} />
            <Input data-testid="stall-where" placeholder="Where are you? Town or postcode"
              value={stall.where} onChange={(e) => setStall({ ...stall, where: e.target.value })} />
            <Input data-testid="stall-about" placeholder="What you grow or bake"
              value={stall.about} onChange={(e) => setStall({ ...stall, about: e.target.value })} />
            <Input data-testid="stall-marketday" placeholder="Market day (optional)"
              value={stall.market_day} onChange={(e) => setStall({ ...stall, market_day: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Delivery radius (miles)
              <Input data-testid="stall-radius" type="number" min={0} max={50} value={stall.delivery_radius_miles}
                onChange={(e) => setStall({ ...stall, delivery_radius_miles: e.target.value })} className="w-20" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Delivery fee
              <Input data-testid="stall-fee" type="number" min={0} step="0.01" value={stall.delivery_fee}
                onChange={(e) => setStall({ ...stall, delivery_fee: e.target.value })} className="w-24" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="stall-organic" checked={stall.organic}
                onChange={(e) => setStall({ ...stall, organic: e.target.checked })} />
              Organic / no sprays
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="stall-collection" checked={stall.collection}
                onChange={(e) => setStall({ ...stall, collection: e.target.checked })} />
              Collection at the gate
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="stall-delivery" checked={stall.delivery}
                onChange={(e) => setStall({ ...stall, delivery: e.target.checked })} />
              I deliver nearby
            </label>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="stall-gps" disabled={locating} onClick={take}>
              <Crosshair size={14} className="mr-1.5" /> Pin the farm here
            </Button>
            <Button size="sm" data-testid="stall-save" disabled={busy || stall.name.trim().length < 2}
              onClick={() => act(async () => {
                await api.post("/farm/stalls", {
                  ...stall,
                  delivery_radius_miles: Number(stall.delivery_radius_miles) || 0,
                  delivery_fee: Number(stall.delivery_fee) || 0,
                  free_delivery_over: Number(stall.free_delivery_over) || 0,
                  lat: where?.lat, lng: where?.lng,
                });
                setStall({ ...stall, name: "", where: "", about: "" });
              }, "Stall opened — it trades once it is checked over")}>
              Open the stall
            </Button>
          </div>
          {(data.my_stalls || []).length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-slate-700" data-testid="stall-mine">
              {data.my_stalls.map((s) => (
                <li key={s.id}>
                  {s.name} · {s.verified ? "trading" : "waiting to be checked over"} ·
                  {" "}{s.symbol} {s.currency?.toUpperCase()}
                  {data.can_manage && !s.verified && (
                    <Button className="ml-2" size="sm" variant="outline" data-testid={`stall-verify-${s.id}`}
                      onClick={() => act(() => api.post(`/farm/stalls/${s.id}/verify`), "Checked over")}>
                      Approve it
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {mine && (
        <div className="border border-border bg-white p-4" data-testid="lot-box">
          <p className="overline text-slate-500">Put produce up with your own price</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Input data-testid="lot-title" placeholder="What is it? 'Half dozen free range eggs'"
              value={lot.title} onChange={(e) => setLot({ ...lot, title: e.target.value })} />
            <select data-testid="lot-kind" value={lot.kind} aria-label="Kind"
              onChange={(e) => setLot({ ...lot, kind: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {data.kinds.map((k) => <option key={k} value={k}>{String(k)}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Your price
              <Input data-testid="lot-price" type="number" min={0} step="0.01" value={lot.price}
                onChange={(e) => setLot({ ...lot, price: e.target.value })} className="w-24" />
            </label>
            <select data-testid="lot-unit" value={lot.unit} aria-label="Per"
              onChange={(e) => setLot({ ...lot, unit: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {data.units.map((u) => <option key={u} value={u}>{`per ${u}`}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              How many
              <Input data-testid="lot-qty" type="number" min={0} value={lot.quantity}
                onChange={(e) => setLot({ ...lot, quantity: e.target.value })} className="w-24" />
            </label>
            <Input data-testid="lot-note" placeholder="Note — 'laid this morning'"
              value={lot.note} onChange={(e) => setLot({ ...lot, note: e.target.value })} />
          </div>
          <div className="mt-3">
            <ImageCaptionField testid="lot-image" label="Photo of the item — goes on the public board"
              value={lot.image_id} caption={lot.image_caption}
              onChange={({ image_id, image_caption }) => setLot({ ...lot, image_id: image_id || "", image_caption })} />
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="lot-price-suggest" disabled={busy}
              onClick={async () => {
                try {
                  const { data: s } = await api.post("/farm/produce/price-suggest", {
                    stall_id: mine.id, kind: lot.kind, title: lot.title, unit: lot.unit });
                  setLot({ ...lot, price: s.price });
                  toast.success(s.line);
                } catch (e) { oops(e); }
              }}>
              <Sparkle size={14} className="mr-1.5" /> What does it fetch round here?
            </Button>
            <Button size="sm" data-testid="lot-save" disabled={busy || lot.title.trim().length < 3}
              onClick={() => act(async () => {
                await api.post("/farm/produce", {
                  stall_id: mine.id, kind: lot.kind, title: lot.title,
                  price: Number(lot.price) || 0, unit: lot.unit,
                  quantity: Number(lot.quantity) || 0, note: lot.note,
                  organic: lot.organic, image_id: lot.image_id || "",
                });
                setLot({ ...lot, title: "", note: "", image_id: "", image_caption: "" });
              }, "On the stall")}>
              Put it on the stall
            </Button>
          </div>
        </div>
      )}

      <div data-testid="farm-produce">
        <p className="overline text-slate-500">On the stalls today</p>
        {data.produce.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nothing on the stalls yet.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {data.produce.map((p) => (
              <div key={p.id} className="border border-border bg-white p-3" data-testid={`produce-${p.id}`}>
                {p.image_url && (
                  <img src={`${BACKEND}${p.image_url}`} alt={p.title} loading="lazy"
                    className="mb-2 h-32 w-full border border-border object-cover"
                    data-testid={`produce-photo-${p.id}`} />
                )}
                <p className="text-sm font-semibold text-[#0F172A]">{p.title}</p>
                <p className="text-xs text-slate-500">
                  {p.kind} · {p.stall_name} · {p.where}
                  {p.organic ? " · organic" : ""} · picked {p.picked_on}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                  {p.symbol}{Number(p.price || 0).toFixed(2)} per {p.unit} · {p.quantity} left
                </p>
                {p.owner_id !== data.me.id && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1 text-xs text-slate-600">
                      Qty
                      <Input data-testid={`produce-qty-${p.id}`} type="number" min={0} className="w-16"
                        value={buy[p.id]?.qty ?? ""} onChange={(e) =>
                          setBuy({ ...buy, [p.id]: { ...buy[p.id], qty: e.target.value } })} />
                    </label>
                    <label className="flex items-center gap-1 text-xs text-slate-600">
                      My price
                      <Input data-testid={`produce-offer-${p.id}`} type="number" min={0} step="0.01" className="w-20"
                        placeholder={String(p.price)} value={buy[p.id]?.offer ?? ""} onChange={(e) =>
                          setBuy({ ...buy, [p.id]: { ...buy[p.id], offer: e.target.value } })} />
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {basket.length > 0 && (
        <div className="border border-[#0B5C2E] bg-[#F3FAF5] p-4" data-testid="farm-basket">
          <p className="overline text-[#0B5C2E]">Your basket — {basket.length} lot(s)</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <select data-testid="basket-how" value={how} aria-label="How"
              onChange={(e) => setHow(e.target.value)}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              <option value="collection">Collect at the gate</option>
              <option value="delivery">Deliver to me</option>
            </select>
            <select data-testid="basket-pay" value={pay} aria-label="Pay"
              onChange={(e) => setPay(e.target.value)}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              <option value="online">Pay online by card</option>
              <option value="on_handover">Pay on handover</option>
            </select>
            {how === "delivery" && (
              <Input data-testid="basket-address" placeholder="Where shall they drop it?"
                value={address} onChange={(e) => setAddress(e.target.value)} />
            )}
          </div>
          <Button className="mt-2" size="sm" data-testid="basket-buy" disabled={busy}
            onClick={() => act(async () => {
              const { data: order } = await api.post("/farm/orders", {
                stall_id: basketStall,
                lines: basket.map(([id, v]) => ({
                  produce_id: id, quantity: Number(v.qty) || 0,
                  offer_price: v.offer === "" || v.offer == null ? null : Number(v.offer),
                })),
                how, address, pay, lat: where?.lat, lng: where?.lng,
              });
              setBuy({});
              if (pay === "online" && order.status === "ordered") {
                const { data: p } = await api.post("/payments/farm/checkout", {
                  order_id: order.id, origin_url: window.location.origin });
                if (p.url) window.location.href = p.url;
              }
            }, "Order placed")}>
            Buy it
          </Button>
        </div>
      )}

      <PickYourOwn data={data} mine={mine} busy={busy} act={act} reload={load} />

      <MilkRound data={data} mine={mine} busy={busy} act={act} where={where} />

      <FreshCatch data={data} mine={mine} busy={busy} act={act} where={where} />

      <FreshMeat data={data} mine={mine} busy={busy} act={act} where={where}
        method={method} setMethod={setMethod} />

      {(data.selling || []).length > 0 && (
        <div data-testid="farm-selling">
          <p className="overline text-slate-500">People buying from you</p>
          <div className="mt-2 space-y-2">
            {data.selling.map((o) => (
              <div key={o.id} className="border border-border bg-white p-3 text-sm" data-testid={`sale-${o.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {o.reference} · {o.name} · {o.symbol}{Number(o.total || 0).toFixed(2)} · {o.status}
                </p>
                <p className="text-xs text-slate-500">
                  {o.lines.map((l) => `${l.quantity}× ${l.title}`).join(", ")} · {o.how}
                  {o.miles != null ? ` · ${o.miles} miles` : ""} · {o.pay === "online" ? "paying online" : "paying on handover"}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {o.status === "offered" && (
                    <>
                      <Button size="sm" data-testid={`sale-accept-${o.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/farm/orders/${o.id}/respond`, { accept: true }), "Offer taken")}>
                        Take the offer
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`sale-decline-${o.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/farm/orders/${o.id}/respond`, { accept: false }), "Turned down")}>
                        No thanks
                      </Button>
                    </>
                  )}
                  {["ordered", "countered"].includes(o.status) && (
                    <Button size="sm" variant="outline" data-testid={`sale-proof-${o.id}`}
                      onClick={() => sendProof(o.id)}>
                      <Camera size={14} className="mr-1.5" /> Send a photo of it picked
                    </Button>
                  )}
                  {o.status !== "handed over" && o.status !== "declined" && (
                    <Button size="sm" variant="outline" data-testid={`sale-handover-${o.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/farm/orders/${o.id}/handed-over`), "Handed over")}>
                      Handed over
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.buying || []).length > 0 && (
        <div data-testid="farm-buying">
          <p className="overline text-slate-500">What you have bought</p>
          <div className="mt-2 space-y-2">
            {data.buying.map((o) => (
              <div key={o.id} className="border border-border bg-white p-3 text-sm" data-testid={`buy-${o.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {o.reference} · {o.stall_name} · {o.symbol}{Number(o.total || 0).toFixed(2)} · {o.status}
                </p>
                <p className="text-xs text-slate-500">
                  {o.lines.map((l) => `${l.quantity}× ${l.title}`).join(", ")} · {o.how} · {o.payment_status}
                </p>
                {o.proof_image_url && (
                  <img src={`${BACKEND}${o.proof_image_url}`} alt="Picked fresh for your order"
                    loading="lazy" className="mt-2 h-32 w-full border border-border object-cover sm:w-48"
                    data-testid={`buy-proof-${o.id}`} />
                )}
                {o.pay === "online" && o.payment_status === "unpaid" && o.status !== "offered" && o.status !== "declined" && (
                  <Button className="mt-2" size="sm" data-testid={`buy-pay-${o.id}`} disabled={busy}
                    onClick={async () => {
                      try {
                        const { data: p } = await api.post("/payments/farm/checkout", {
                          order_id: o.id, origin_url: window.location.origin });
                        if (p.url) window.location.href = p.url;
                      } catch (e) { oops(e); }
                    }}>
                    Pay now
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
